import React, { useContext, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import LeadsStepper from '../../components/LeadsStepper';
import LeadsDragContainers from '../../components/LeadsDragContainers';
import LeadsContext from '../../providers/Leads/Leads.context';
import { Loader } from 'ncoded-component-library';
import ListingContext from '../../providers/Listing/Listing.context';
import HelmetWrapper from 'components/HelmetWrapper';
import useQueryParams from 'hooks/useQueryParams';
import { LeadDrawerOptions } from '../../../../models';
import { getIsArrayEmpty } from 'utils';
import LeadsTableContainer from '../../components/LeadsTableContainer';
import { GetBuyersBanner } from 'components/GetBuyersBanner';
import DealContext from '../../providers/Deal/Deal.context';
import LeadsEmptyState from './components/LeadsEmptyState';
import LeadsActions from './components/LeadsActions';
import useMatchMedia from 'hooks/useMatchMedia';
import LeadsMobileList from '../../components/LeadsMobileList';
import api from 'api';
import { getLeadsIds } from '../../utils';
import useLeads from '../../hooks/useLeads';
import { FIVE_SECONDS } from 'data/global';

import './PortfolioLeads.styles.scss';
import './PortfolioLeads.styles.responsive.scss';

type LeadsParams = {
  leadId: string;
  open: LeadDrawerOptions;
  highlightLeadId: string;
};

type PortfolioLeadsProps = {
  className?: string;
};

const PortfolioLeads: React.FC<PortfolioLeadsProps> = (props) => {
  const { className } = props;
  const classes = classNames('portfolio-leads', className);

  const {
    params: { leadId, open, highlightLeadId },
    removeQueryParam,
    removeSeveralQueryParams,
  } = useQueryParams<LeadsParams>();

  const { showGetBuyers } = useContext(DealContext);
  const {
    isLoadingGrid,
    isLoadingTable,
    tableHasAnyLeads,
    gridHasAnyLeads,
    hasAnyLeads,
    gridLeads,
    selectedLead,
    scrollableContainerRef,
    openLeadDrawer,
    updateLeads,
  } = useContext(LeadsContext);
  const { dealRoom, listing, isTableView, areActionsDisabled } =
    useContext(ListingContext);

  const { getNewLeads } = useLeads();

  const isAboveLaptop = useMatchMedia('isAboveLaptop');

  const isLoading = useMemo(
    () =>
      isTableView
        ? isLoadingTable && !tableHasAnyLeads
        : isLoadingGrid && !gridHasAnyLeads,
    [
      gridHasAnyLeads,
      isLoadingGrid,
      isLoadingTable,
      isTableView,
      tableHasAnyLeads,
    ],
  );

  const showContent = useMemo(
    () => (isTableView ? tableHasAnyLeads : gridHasAnyLeads),
    [gridHasAnyLeads, isTableView, tableHasAnyLeads],
  );

  const renderContent = useMemo(
    () => (
      <>
        {!isAboveLaptop ? (
          <LeadsMobileList />
        ) : isTableView ? (
          <LeadsTableContainer />
        ) : (
          <>
            <LeadsStepper canEditColumnName={dealRoom?.isRWC} />
            <LeadsDragContainers />
          </>
        )}
        <LeadsActions />
      </>
    ),
    [isAboveLaptop, isTableView, dealRoom?.isRWC],
  );

  useEffect(() => {
    if (leadId && hasAnyLeads) {
      const lead = Object.values(gridLeads)
        .flat()
        .find(({ _id }) => _id === leadId);

      if (lead && !selectedLead) {
        if (open) openLeadDrawer(lead, open);

        removeSeveralQueryParams(['leadId', 'open']);
      }
    }
  }, [
    leadId,
    selectedLead,
    gridLeads,
    hasAnyLeads,
    open,
    openLeadDrawer,
    removeSeveralQueryParams,
  ]);

  useEffect(() => {
    if (highlightLeadId) {
      setTimeout(() => removeQueryParam('highlightLeadId'), FIVE_SECONDS);
    }
  }, [highlightLeadId, removeQueryParam]);

  useEffect(() => {
    (async () => {
      const newLeads = Object.values(gridLeads).flatMap(getNewLeads);

      if (getIsArrayEmpty(newLeads)) return;

      setTimeout(async () => {
        await api.portfolio.seller.viewNewLeads(
          dealRoom?._id,
          getLeadsIds(newLeads),
        );

        updateLeads(newLeads);
      }, FIVE_SECONDS);
    })();
  }, [dealRoom?._id, gridLeads, getNewLeads, updateLeads]);

  return (
    <div className={classes} ref={scrollableContainerRef}>
      <HelmetWrapper title={`Leads - ${dealRoom?.name}`} />

      {isLoading ? (
        <div className="portfolio-leads__loading">
          <Loader overlay={false} />
        </div>
      ) : showContent ? (
        renderContent
      ) : (
        <LeadsEmptyState />
      )}

      {showGetBuyers && (
        <GetBuyersBanner
          disabled={areActionsDisabled}
          dealRoomId={dealRoom?._id}
          listingId={listing?._id}
          listingType={listing?.type}
          hasImage={false}
        />
      )}
    </div>
  );
};

export default PortfolioLeads;
